import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import LeadTable from '../../../components/tables/LeadTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import {
  LeadClient,
  LeadDTO,
  SearchRequestDTO_leadSearchRequestDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/config';
import axiosInstance from '../../../services/axiosInstance';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { getUsers } from '../../../hooks/useUsers';
import { getRoles } from '../../../utils/tokenUtils';

const LeadList: React.FC = () => {
  const [items, setItems] = useState<LeadDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhoneNumber, setSearchPhoneNumber] = useState('');
  const [searchCompanyName, setSearchCompanyName] = useState('');
  const [searchCreatedBy, setSearchCreatedBy] = useState('');

  const isAdmin = getRoles()?.includes('Administrator');
  const { users } = getUsers();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Leads', href: '#' },
  ];

  const apiClient = new LeadClient(API_BASE_URL, axiosInstance);

  const fetchLeads = async (page: number, size: number, filters = {}) => {
    const searchRequest: SearchRequestDTO_leadSearchRequestDTO = {
      filters: {
        firstName: searchFirstName || '',
        lastName: searchLastName || '',
        email: searchEmail || '',
        phoneNumber: searchPhoneNumber || '',
        companyName: searchCompanyName || '',
        createdBy: searchCreatedBy || undefined,
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'CreatedUtc',
      sortDescending: true,
    };

    try {
      setIsLoading(true);
      const response = await apiClient.searchLeads(searchRequest);
      const totalRecords = response.data?.totalCount ?? 0;

      setItems((response.data?.results as LeadDTO[]) || []);
      setTotalRecords(totalRecords);
      setTotalPages(Math.ceil(totalRecords / size));
    } catch (error) {
      console.error(`Error fetching Leads:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const ExportItems = async (
    page: number,
    size: number,
    filters = {},
  ): Promise<void> => {
    const searchRequest: SearchRequestDTO_leadSearchRequestDTO = {
      filters: {
        firstName: searchFirstName || '',
        lastName: searchLastName || '',
        email: searchEmail || '',
        phoneNumber: searchPhoneNumber || '',
        companyName: searchCompanyName || '',
        createdBy: searchCreatedBy || undefined,
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'CreatedUtc',
      sortDescending: true,
    };

    try {
      setIsLoading(true);
      const response = await apiClient.exportLeads(searchRequest);
      if (response && response.success) {
        // Download the file
        // Assuming the response.data contains the URL to the exported file
        window.open(response.data, '_blank');
      }
    } catch (error) {
      console.error(`Error fetching Members:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLeads(1, pageSize);
  };

  const handleClearSearch = () => {
    setSearchFirstName('');
    setSearchLastName('');
    setSearchEmail('');
    setSearchPhoneNumber('');
    setSearchCompanyName('');
    setSearchCreatedBy('');
    fetchLeads(1, pageSize, {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      createdBy: undefined,
    });
  };

  const handleDelete = async (dto: LeadDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await apiClient.deleteLead(dto.id || '');
      await fetchLeads(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleExport = (): void => {
    setCurrentPage(1);
    ExportItems(1, pageSize);
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/leads"

      breadcrumbs={breadcrumbs}
      allowedRoles={['Administrator', 'User']}
    >
      <Card title="Leads">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-500 dark:text-gray-300">
            Manage Leads ({totalRecords})
          </h2>
          <div className="ml-auto">
            <AddNewButton onClick={() => navigate('/admin/leads/create')} />
          </div>
        </div>

        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="firstName"
              label="First Name"
              value={searchFirstName}
              onChange={(e) => setSearchFirstName(e.target.value)}
              placeholder="Search by first name"
            />
            <FormField
              id="lastName"
              label="Last Name"
              value={searchLastName}
              onChange={(e) => setSearchLastName(e.target.value)}
              placeholder="Search by last name"
            />
            <FormField
              id="email"
              label="Email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="Search by email"
            />
            <FormField
              id="phoneNumber"
              label="Phone Number"
              value={searchPhoneNumber}
              onChange={(e) => setSearchPhoneNumber(e.target.value)}
              placeholder="Search by phone number"
            />
            <FormField
              id="companyName"
              label="Company Name"
              value={searchCompanyName}
              onChange={(e) => setSearchCompanyName(e.target.value)}
              placeholder="Search by company name"
            />
            {isAdmin && (
              <FormField
                id="createdBy"
                label="Rep"
                type="dropdown"
                value={searchCreatedBy}
                onChange={(e) => setSearchCreatedBy(e.target.value)}
                options={users.map((user) => ({
                  value: user.id,
                  label: user.firstName || '',
                }))}
              />
            )}
          </div>

          <div className="flex justify-between items-center mb-4">
            {/* Left-side buttons */}
            <div className="flex space-x-4">
              <Button variant="secondary" onClick={handleClearSearch}>
                Clear Search
              </Button>
              <Button type="submit">Search</Button>
            </div>

            {/* Right-side Export button */}
            <Button onClick={handleExport}>Export</Button>
          </div>
        </form>

        <LeadTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/leads/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default LeadList;
